export interface ContentfulArrayContract<
    T extends ContentfulEntryContract = ContentfulEntryContract,
> {
    includes?: ContentfulArrayIncludesContract;
    items: T[];
    limit: number;
    skip: number;
    sys: { type: 'Array' };
    total: number;
}

export interface ContentfulArrayIncludesContract {
    Asset?: ContentfulAssetContract[];
    Entry?: ContentfulEntryContract[];
}

export interface ContentfulEntryMetadataContract {
    concepts: unknown[];
    tags: unknown[];
}

export interface ContentfulEntrySysContract<T extends string = string> {
    contentType?: ContentTypeLink<T>;
    createdAt?: string;
    environment?: EnvironmentLink;
    firstPublishedAt?: string;
    id?: string;
    locale?: string;
    publishedAt?: string;
    publishedVersion?: number;
    revision?: number;
    space?: SpaceLink;
    type?: string;
    updatedAt?: string;
}

export type ContentfulEntryContractFields = Record<
    string,
    | string
    | string[]
    | FileField
    | RichTextNodeContract
    | AssetLink
    | AssetLink[]
    | EntryLink
    | EntryLink[]
    | object
    | undefined
>;

export type ContentfulAssetFields = {
    title?: string;
    description?: string;
    file?: FileField;
} & Record<string, string | string[] | FileField | undefined>;

type ResolveContentfulFields<T> = {
    [K in keyof T]: T[K] extends RichTextNodeContract | undefined
        ? RichTextNode | undefined
        : T[K] extends AssetLink | undefined
          ? ContentfulAssetContract | undefined
          : T[K] extends AssetLink[] | undefined
            ? ContentfulAssetContract[] | undefined
            : T[K] extends EntryLink | undefined
              ? ContentfulEntry<ContentfulAssetFields> | undefined
              : T[K] extends EntryLink[] | undefined
                ? ContentfulEntry<ContentfulAssetFields>[] | undefined
                : T[K];
};

export type ContentfulEntryFields = Record<
    string,
    | string
    | string[]
    | FileField
    | RichTextNode
    | ContentfulAssetContract
    | ContentfulAssetContract[]
    | ContentfulEntry<ContentfulAssetFields>
    | ContentfulEntry<ContentfulAssetFields>[]
    | object
    | undefined
>;

export interface ContentfulEntryContract<
    F extends ContentfulEntryContractFields = ContentfulEntryContractFields,
    T extends string = string,
> {
    fields: F;
    metadata: ContentfulEntryMetadataContract;
    sys: ContentfulEntrySysContract<T>;
}

export type ContentfulAssetContract =
    ContentfulEntryContract<ContentfulAssetFields>;

export interface ContentfulEntry<
    F extends ContentfulEntryFields = ContentfulEntryFields,
    T extends string = string,
> {
    fields: F;
    metadata: ContentfulEntryMetadataContract;
    sys: ContentfulEntrySysContract<T>;
}

export type ContentfulEntryFromContract<T extends ContentfulEntryContract> =
    T extends ContentfulEntryContract<infer F, infer ContentType>
        ? ContentfulEntry<ResolveContentfulFields<F>, ContentType>
        : never;

export interface LinkObject<T extends string = string> {
    sys: {
        id: string;
        linkType: T;
        type: 'Link';
    };
}

export type AssetLink = LinkObject<'Asset'>;

export interface ContentTypeLink<T extends string = string> {
    sys: {
        id: T;
        linkType: 'ContentType';
        type: string;
    };
}

export type EntryLink = LinkObject<'Entry'>;

export type EnvironmentLink = LinkObject<'Environment'>;

export type SpaceLink = LinkObject<'Space'>;

export interface RichTextNodeContract {
    content?: RichTextNodeContract[];
    data?: {
        target?:
            | {
                  fields?: {
                      description?: string;
                      file?: {
                          details?: {
                              image?: {
                                  height?: number;
                                  width?: number;
                              };
                          };
                          url?: string;
                      };
                  };
              }
            | LinkObject;
        uri?: string;
    };
    marks?: Array<{
        type: string;
    }>;
    nodeType?: string;
    value?: string;
}

export interface RichTextNode extends RichTextNodeContract {
    content?: RichTextNode[];
    data?: {
        target?: {
            fields?: {
                description?: string;
                file?: {
                    details?: {
                        image?: {
                            height?: number;
                            width?: number;
                        };
                    };
                    url?: string;
                };
            };
        };
        uri?: string;
    };
}

export type FileField = {
    contentType?: string;
    details?: {
        image?: {
            height?: number;
            width?: number;
        };
        size?: number;
    };
    fileName?: string;
    url?: string;
};

export function isLinkObject(thing: unknown): thing is LinkObject {
    return (
        thing !== null &&
        typeof thing === 'object' &&
        'sys' in thing &&
        thing.sys !== null &&
        typeof thing.sys === 'object' &&
        'type' in thing.sys &&
        thing.sys.type === 'Link' &&
        'id' in thing.sys &&
        typeof thing.sys.id === 'string' &&
        'linkType' in thing.sys &&
        typeof thing.sys.linkType === 'string'
    );
}

export function isContentfulEntrySysContract(
    thing: unknown
): thing is ContentfulEntrySysContract {
    return (
        thing !== null &&
        typeof thing === 'object' &&
        'space' in thing &&
        isLinkObject(thing.space) &&
        'contentType' in thing &&
        isLinkObject(thing.contentType)
    );
}

export function isContentfulEntry(thing: unknown): thing is ContentfulEntry {
    return isContentfulEntrySysContract(thing) && thing.type === 'Entry';
}

export function isAssetLink(thing: unknown): thing is AssetLink {
    return isLinkObject(thing) && thing.sys.linkType === 'Asset';
}

export function isEntryLink(thing: unknown): thing is EntryLink {
    return isLinkObject(thing) && thing.sys.linkType === 'Entry';
}

export function isRichTextNodeContract(
    thing: unknown
): thing is RichTextNodeContract {
    return typeof thing === 'object' && thing !== null && 'nodeType' in thing;
}

export function isFileField(thing: unknown): thing is FileField {
    return typeof thing === 'object' && thing !== null && 'fileName' in thing;
}
